import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    ShieldCheck,
    Clock,
    CheckCircle2,
    AlertTriangle,
    Loader2,
    ArrowLeft,
    Mail,
    Calendar,
    User,
    Cpu,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";

/**
 * Admin → Compliance history page.
 *
 * Lists the most recent quarterly-digest sends so admins can audit:
 *   - WHO triggered it (manual admin actor vs. system-scheduler / auto)
 *   - WHEN (UTC timestamp)
 *   - WHAT quarter the digest covered
 *   - WHETHER it was a dry-run
 *   - HOW MANY recipients received it (and how many failed)
 *
 * Data source: GET /api/admin/compliance/history?limit=10
 */
export default function AdminComplianceHistoryPage() {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!authLoading && user?.role !== "admin") {
            navigate("/");
        }
    }, [user, authLoading, navigate]);

    useEffect(() => {
        const load = async () => {
            try {
                const { data } = await api.get("/admin/compliance/history", { params: { limit: 10 } });
                setItems(data.items || []);
            } catch (e) {
                toast.error(e.response?.data?.detail || "Verlauf konnte nicht geladen werden");
            } finally {
                setLoading(false);
            }
        };
        if (user?.role === "admin") load();
    }, [user]);

    if (authLoading || user?.role !== "admin") {
        return (
            <div className="flex items-center justify-center min-h-[40vh]">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto px-4 py-8" data-testid="admin-compliance-history-page">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <ShieldCheck className="w-7 h-7 text-[#FFCC00]" />
                    <div>
                        <h1 className="font-display text-2xl font-bold">Compliance-Verlauf</h1>
                        <p className="text-sm text-gray-600">Letzte 10 Versand-Ereignisse des Quartals-Digests.</p>
                    </div>
                </div>
                <Link
                    to="/admin/compliance"
                    className="inline-flex items-center gap-2 text-sm font-bold text-gray-700 hover:text-[#111827]"
                    data-testid="back-to-compliance-link"
                >
                    <ArrowLeft className="w-4 h-4" /> Zurück zu Einstellungen
                </Link>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-16">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
            ) : items.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-sm p-8 text-center" data-testid="history-empty">
                    <Mail className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">Bisher wurden keine Quartals-Digests versendet.</p>
                </div>
            ) : (
                <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
                    <table className="w-full text-sm" data-testid="history-table">
                        <thead className="bg-gray-50 border-b border-gray-200 text-[10px] uppercase tracking-wider text-gray-600">
                            <tr>
                                <th className="text-left px-4 py-3">Zeitstempel</th>
                                <th className="text-left px-4 py-3">Quartal</th>
                                <th className="text-left px-4 py-3">Auslöser</th>
                                <th className="text-left px-4 py-3">Modus</th>
                                <th className="text-right px-4 py-3">Empfänger</th>
                                <th className="text-right px-4 py-3">Erfolgreich</th>
                                <th className="text-right px-4 py-3">Fehler</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((it, idx) => (
                                <tr
                                    key={`${it.at}-${idx}`}
                                    className="border-b border-gray-100 last:border-b-0 hover:bg-[#FFFBEB]/40"
                                    data-testid="history-row"
                                >
                                    <td className="px-4 py-3 font-mono-num text-xs text-gray-700 flex items-center gap-1.5">
                                        <Clock className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                        {it.at ? new Date(it.at).toLocaleString("de-DE") : "—"}
                                    </td>
                                    <td className="px-4 py-3 font-bold text-[#111827]">
                                        <Calendar className="inline-block w-3.5 h-3.5 text-gray-400 mr-1" />
                                        {it.label || "—"}
                                    </td>
                                    <td className="px-4 py-3">
                                        {it.trigger === "auto" ? (
                                            <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 font-bold text-[10px]">
                                                <Cpu className="inline-block w-3 h-3 mr-1" /> AUTO
                                            </Badge>
                                        ) : (
                                            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 font-bold text-[10px]">
                                                <User className="inline-block w-3 h-3 mr-1" /> MANUELL
                                            </Badge>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        {it.dry_run ? (
                                            <Badge className="bg-gray-100 text-gray-700 hover:bg-gray-100 font-bold text-[10px]">
                                                DRY-RUN
                                            </Badge>
                                        ) : (
                                            <Badge className="bg-green-100 text-green-800 hover:bg-green-100 font-bold text-[10px]">
                                                <CheckCircle2 className="inline-block w-3 h-3 mr-1" /> VERSAND
                                            </Badge>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-right font-mono-num">{it.recipients}</td>
                                    <td className="px-4 py-3 text-right font-mono-num text-green-700 font-bold">{it.sent}</td>
                                    <td className="px-4 py-3 text-right font-mono-num">
                                        {it.skipped_count > 0 ? (
                                            <span className="text-red-700 font-bold inline-flex items-center gap-1">
                                                <AlertTriangle className="w-3.5 h-3.5" />
                                                {it.skipped_count}
                                            </span>
                                        ) : (
                                            <span className="text-gray-400">0</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
